import {sanityClient} from "./client";
import {
  ContentCategory,
  ContentItem,
  GivingPage,
  HomePage,
  ResourcesPage,
  SiteSettings,
} from "./types";

const cache = new Map<
  string,
  {
    timestamp: number;
    data: unknown;
    promise?: Promise<unknown>;
  }
>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const STORAGE_KEY_PREFIX = "sanity-cache::";

const isBrowser = typeof window !== "undefined";
const getStorage = () => {
  if (!isBrowser) return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const buildCacheKey = (query: string, params?: Record<string, unknown>) =>
  `${query}::${JSON.stringify(params || {})}`;

const getCacheEntry = (key: string) => {
  const entry = cache.get(key);
  if (!entry) return null;
  const isExpired = Date.now() - entry.timestamp > CACHE_TTL_MS;
  if (isExpired) {
    cache.delete(key);
    return null;
  }
  return entry;
};

const getCached = <T>(key: string): {hit: boolean; value?: T} => {
  const entry = getCacheEntry(key);
  if (!entry || entry.data === undefined) return {hit: false};
  return {hit: true, value: entry.data as T};
};

const setCached = (key: string, data: unknown) => {
  // avoid persisting empty/null responses; they don't help reload UX
  if (data === null || data === undefined) return;
  cache.set(key, {timestamp: Date.now(), data});
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(
      STORAGE_KEY_PREFIX + key,
      JSON.stringify({timestamp: Date.now(), data})
    );
  } catch {
    /* ignore storage errors */
  }
};

const getCachedFromStorage = <T>(key: string): {hit: boolean; value?: T} => {
  const storage = getStorage();
  if (!storage) return {hit: false};
  try {
    const raw = storage.getItem(STORAGE_KEY_PREFIX + key);
    if (!raw) return {hit: false};
    const parsed = JSON.parse(raw) as {timestamp: number; data: T};
    const isExpired = Date.now() - parsed.timestamp > CACHE_TTL_MS;
    if (isExpired) {
      storage.removeItem(STORAGE_KEY_PREFIX + key);
      return {hit: false};
    }
    // hydrate in-memory cache for faster subsequent lookups
    cache.set(key, {timestamp: parsed.timestamp, data: parsed.data});
    return {hit: true, value: parsed.data};
  } catch {
    return {hit: false};
  }
};

export const getCachedHomePage = (): HomePage | null => {
  const key = buildCacheKey(homePageQuery);
  const mem = getCached<HomePage>(key);
  if (mem.hit) return mem.value ?? null;
  const storage = getCachedFromStorage<HomePage>(key);
  return storage.hit ? storage.value ?? null : null;
};

const homePageQuery = `
*[_type == "homePage"][0]{
  title,
  metaTitle,
  metaDescription,
  highlights[]{
    _key,
    title,
    body,
    image{
      ...,
      asset->,
      alt
    }
  }
}`;

const contentItemFields = `
  _id,
  title,
  "slug": slug.current,
  excerpt,
  body,
  publishedAt,
  image{
    ...,
    asset->,
    alt
  },
  metaTitle,
  metaDescription
`;

const latestNewsItemsQuery = `
*[
  _type == "latestNewsItem" ||
  (_type == "contentItem" && category == "latestNews")
]
  | order(publishedAt desc, title asc) {
    ${contentItemFields},
    "category": "latestNews"
  }`;

const ministryItemsQuery = `
*[
  _type == "ministryItem" ||
  (_type == "contentItem" && category == "ministry")
]
  | order(publishedAt desc, title asc) {
    ${contentItemFields},
    "category": "ministry"
  }`;

const latestNewsItemBySlugQuery = `
*[
  (_type == "latestNewsItem" && slug.current == $slug) ||
  (_type == "contentItem" && category == "latestNews" && slug.current == $slug)
][0]{
  ${contentItemFields},
  "category": "latestNews"
}`;

const ministryItemBySlugQuery = `
*[
  (_type == "ministryItem" && slug.current == $slug) ||
  (_type == "contentItem" && category == "ministry" && slug.current == $slug)
][0]{
  ${contentItemFields},
  "category": "ministry"
}`;

const siteSettingsQuery = `
*[_type == "siteSettings"][0]{
  address,
  addressLink,
  phone,
  email,
  missionStatement,
  socialLinks{
    x,
    facebook,
    youtube,
    soundcloud
  }
}`;

const givingPageQuery = `
*[_type == "givingPage"][0]{
  title,
  giveOnlineHeading,
  giveOnlineImage{
    ...,
    asset->,
    alt
  },
  mailHeading,
  mailAddressLines,
  inPersonHeading,
  inPersonText,
  metaTitle,
  metaDescription
}`;

const resourcesPageQuery = `
*[_type == "resourcesPage"][0]{
  title,
  image{
    ...,
    asset->,
    alt
  },
  metaTitle,
  metaDescription
}`;

const fetchFromSanity = <T>(
  query: string,
  params?: Record<string, unknown>
) => {
  const key = buildCacheKey(query, params);
  const cached = getCached<T>(key);
  if (cached.hit) return Promise.resolve(cached.value as T);
  const storageCached = getCachedFromStorage<T>(key);
  if (storageCached.hit) return Promise.resolve(storageCached.value as T);

  const entry = getCacheEntry(key);
  if (entry?.promise) {
    return entry.promise as Promise<T>;
  }

  const fetchWithRetry = async () => {
    let attempts = 0;
    while (attempts < 2) {
      try {
        const data = await sanityClient.fetch<T>(query, params);
        if (data === null || data === undefined) {
          cache.delete(key);
          return data;
        }
        setCached(key, data);
        return data;
      } catch (err) {
        attempts += 1;
        if (attempts >= 2) {
          cache.delete(key);
          throw err;
        }
      }
    }
    cache.delete(key);
    return null as unknown as T;
  };

  const fetchPromise = fetchWithRetry();
  cache.set(key, {timestamp: Date.now(), data: undefined, promise: fetchPromise});
  return fetchPromise;
};

export const getHomePage = () =>
  fetchFromSanity<HomePage | null>(homePageQuery);

const contentItemsByCategoryQueries: Record<ContentCategory, string> = {
  latestNews: latestNewsItemsQuery,
  ministry: ministryItemsQuery,
};

export const getContentItemsByCategory = (category: ContentCategory) =>
  fetchFromSanity<ContentItem[]>(contentItemsByCategoryQueries[category]);

const contentItemBySlugQueries: Record<ContentCategory, string> = {
  latestNews: latestNewsItemBySlugQuery,
  ministry: ministryItemBySlugQuery,
};

export const getContentItemBySlug = (
  category: ContentCategory,
  slug: string
) =>
  fetchFromSanity<ContentItem | null>(
    contentItemBySlugQueries[category],
    {slug}
  );

export const getSiteSettings = () =>
  fetchFromSanity<SiteSettings | null>(siteSettingsQuery);

export const getGivingPage = () =>
  fetchFromSanity<GivingPage | null>(givingPageQuery);

export const getResourcesPage = () =>
  fetchFromSanity<ResourcesPage | null>(resourcesPageQuery);
